import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, inject, ViewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { ChipModule } from 'primeng/chip';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { PropertyDefinition } from '../../models/property-schema';
import { PreventOverlayCloseOnScrollDirective } from '../../directives/prevent-overlay-close-on-scroll';

@Component({
  selector: 'app-openapi-properties-editor',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    TableModule,
    DrawerModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    ToggleSwitchModule,
    SelectButtonModule,
    TooltipModule,
    ChipModule,
    MenuModule,
    PreventOverlayCloseOnScrollDirective
],
  templateUrl: './openapi-properties-editor.html',
  styleUrl: './openapi-properties-editor.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenapiPropertiesEditorComponent {
  @Input() properties: PropertyDefinition[] = [];
  @Input() readonlyProperties: PropertyDefinition[] = [];
  @Input() allowLlmFill: boolean = false;
  @Input() title: string = 'Properties';
  @Input() allowedTypes: Array<'string' | 'integer' | 'number' | 'boolean' | 'enum' | 'object' | 'array'> = ['string', 'integer', 'number', 'boolean', 'enum', 'object', 'array'];
  @Input() showAddButton: boolean = true;
  @Input() showOrderButtons: boolean = true;
  @Input() showDeleteButton: boolean = true;
  @Input() showEmptyState: boolean = true;
  @Input() showTitleWhenEmpty: boolean = true;
  @Output() propertiesChange = new EventEmitter<PropertyDefinition[]>();
  @Output() propertyNameClick = new EventEmitter<PropertyDefinition>();

  private cdr = inject(ChangeDetectorRef);

  // Drawer state
  showPropertyDrawer = false;
  editingProperty: PropertyDefinition | null = null;
  editingPropertyIndex = -1;

  // Menu
  @ViewChild('propertyMenu') propertyMenu!: Menu;
  propertyMenuItems: MenuItem[] = [];
  currentPropertyIndex = -1;

  allPropertyTypes = [
    { label: $localize`:@@openapi.type.string:String`, value: 'string' },
    { label: $localize`:@@openapi.type.integer:Integer`, value: 'integer' },
    { label: $localize`:@@openapi.type.number:Number`, value: 'number' },
    { label: $localize`:@@openapi.type.boolean:Boolean`, value: 'boolean' },
    { label: $localize`:@@openapi.type.enum:Enum`, value: 'enum' },
    { label: $localize`:@@openapi.type.object:Object`, value: 'object' },
    { label: $localize`:@@openapi.type.array:Array`, value: 'array' }
  ];

  arrayItemTypes = [
    { label: $localize`:@@openapi.type.string:String`, value: 'string' },
    { label: $localize`:@@openapi.type.integer:Integer`, value: 'integer' },
    { label: $localize`:@@openapi.type.number:Number`, value: 'number' },
    { label: $localize`:@@openapi.type.boolean:Boolean`, value: 'boolean' },
    { label: $localize`:@@openapi.type.enum:Enum`, value: 'enum' },
    { label: $localize`:@@openapi.type.object:Object`, value: 'object' }
  ];

  get propertyTypes() {
    return this.allPropertyTypes.filter(type => this.allowedTypes.includes(type.value as any));
  }

  fillModeOptions = [
    { label: $localize`:@@openapi.fillMode.constant:Fixed Value`, value: 'constant', icon: 'pi pi-lock' },
    { label: $localize`:@@openapi.fillMode.llm:AI Dynamic`, value: 'llm', icon: 'pi pi-sparkles' }
  ];

  readonly enumTypeOptions = [
    { label: $localize`:@@openapi.type.string:String`, value: 'string' },
    { label: $localize`:@@openapi.type.integer:Integer`, value: 'integer' },
    { label: $localize`:@@openapi.type.number:Number`, value: 'number' }
  ];

  readonly addPropertyTitle = $localize`:@@openapi.addProperty:Add Property`;
  readonly editPropertyTitle = $localize`:@@openapi.editProperty:Edit Property`;
  readonly itemPropertiesLabel = $localize`:@@openapi.itemProperties:Item Properties`;
  readonly nestedPropertiesLabel = $localize`:@@openapi.nestedProperties:Nested Properties`;
  readonly valueIsNullLabel = $localize`:@@openapi.valueIsNull:value is null`;
  readonly defaultValuePlaceholder = $localize`:@@openapi.defaultValue.placeholder:Default value`;
  readonly selectDefaultValuePlaceholder = $localize`:@@openapi.selectDefaultValue:Select default value`;

  addProperty() {
    const defaultType = this.allowedTypes[0] || 'string';
    this.editingProperty = {
      name: `property${this.properties.length + 1}`,
      title: '',
      type: defaultType,
      nullable: false,
      default: this.getDefaultValueForType(defaultType),
      fillMode: this.allowLlmFill ? 'constant' : undefined
    };

    // Initialize object/array specific fields
    if (defaultType === 'object') {
      this.editingProperty.properties = [];
    } else if (defaultType === 'array') {
      this.editingProperty.items = { type: 'string' };
      this.editingProperty.minItems = null;
      this.editingProperty.maxItems = null;
    }

    this.editingPropertyIndex = -1;
    this.showPropertyDrawer = true;
    this.cdr.markForCheck();
  }

  private getDefaultValueForType(type: string): any {
    switch (type) {
      case 'string': return '';
      case 'integer':
      case 'number': return 0;
      case 'boolean': return false;
      case 'enum': return null;
      case 'object': return {};
      case 'array': return [];
      default: return null;
    }
  }

  editProperty(index: number) {
    this.editingProperty = JSON.parse(JSON.stringify(this.properties[index]));
    this.editingPropertyIndex = index;
    this.showPropertyDrawer = true;
    this.cdr.markForCheck();
  }

  savePropertyEdit() {
    if (!this.editingProperty) return;

    const updatedProperties = [...this.properties];

    if (this.editingPropertyIndex === -1) {
      updatedProperties.push(this.editingProperty);
    } else {
      updatedProperties[this.editingPropertyIndex] = this.editingProperty;
    }

    this.propertiesChange.emit(updatedProperties);
    this.showPropertyDrawer = false;
    this.editingProperty = null;
    this.editingPropertyIndex = -1;
    this.cdr.markForCheck();
  }

  cancelPropertyEdit() {
    this.showPropertyDrawer = false;
    this.editingProperty = null;
    this.editingPropertyIndex = -1;
    this.cdr.markForCheck();
  }

  removeProperty(index: number) {
    const updatedProperties = this.properties.filter((_, i) => i !== index);
    this.propertiesChange.emit(updatedProperties);
    this.cdr.markForCheck();
  }

  movePropertyUp(index: number) {
    if (index === 0) return;
    const updatedProperties = [...this.properties];
    [updatedProperties[index - 1], updatedProperties[index]] = [updatedProperties[index], updatedProperties[index - 1]];
    this.propertiesChange.emit(updatedProperties);
    this.cdr.markForCheck();
  }

  movePropertyDown(index: number) {
    if (index === this.properties.length - 1) return;
    const updatedProperties = [...this.properties];
    [updatedProperties[index], updatedProperties[index + 1]] = [updatedProperties[index + 1], updatedProperties[index]];
    this.propertiesChange.emit(updatedProperties);
    this.cdr.markForCheck();
  }

  onPropertyTypeChange(property: PropertyDefinition) {
    // Reset default value when type changes
    switch (property.type) {
      case 'string':
        property.default = '';
        delete property.properties;
        delete property.items;
        delete property.minItems;
        delete property.maxItems;
        break;
      case 'integer':
      case 'number':
        property.default = 0;
        delete property.properties;
        delete property.items;
        delete property.minItems;
        delete property.maxItems;
        break;
      case 'boolean':
        property.default = false;
        delete property.properties;
        delete property.items;
        delete property.minItems;
        delete property.maxItems;
        break;
      case 'enum':
        property.enum = [{ label: 'Option 1', value: 'option1' }];
        property.default = 'option1';
        property.enumType = 'string';
        delete property.properties;
        delete property.items;
        delete property.minItems;
        delete property.maxItems;
        break;
      case 'object':
        property.default = {};
        property.properties = [];
        delete property.items;
        delete property.minItems;
        delete property.maxItems;
        break;
      case 'array':
        property.default = [];
        property.items = { type: 'string' };
        property.minItems = null;
        property.maxItems = null;
        delete property.properties;
        break;
    }
    this.cdr.markForCheck();
  }

  onNullableChange(property: PropertyDefinition) {
    if (property.nullable) {
      // Initialize isDefaultNull if not set
      if (property.isDefaultNull === undefined) {
        property.isDefaultNull = false;
      }
    } else {
      // Clear isDefaultNull when nullable is disabled
      property.isDefaultNull = false;
      // Reset to type-specific default
      this.onPropertyTypeChange(property);
    }
    this.cdr.markForCheck();
  }

  onIsDefaultNullChange(property: PropertyDefinition) {
    if (property.isDefaultNull) {
      // When enabled, clear the default value
      property.default = null;
    } else {
      // When disabled, reset to type-specific default
      property.default = this.getDefaultValueForType(property.type);
    }
    this.cdr.markForCheck();
  }

  onFillModeChange(property: PropertyDefinition) {
    if (property.fillMode === 'llm') {
      // Clear default value when switching to LLM mode
      property.default = undefined;
    } else {
      // Set type-specific default when switching to constant mode
      this.onPropertyTypeChange(property);
    }
    this.cdr.markForCheck();
  }

  addEnumValue(property: PropertyDefinition) {
    if (!property.enum) {
      property.enum = [];
    }
    const valueIndex = property.enum.length + 1;

    // Create value based on enumType
    let defaultValue: any;
    if (property.enumType === 'integer' || property.enumType === 'number') {
      defaultValue = valueIndex;
    } else {
      defaultValue = `option${valueIndex}`;
    }

    property.enum.push({ label: `Option ${valueIndex}`, value: defaultValue });
    this.cdr.markForCheck();
  }

  removeEnumValue(property: PropertyDefinition, index: number) {
    if (property.enum) {
      property.enum.splice(index, 1);
      this.cdr.markForCheck();
    }
  }

  onEnumTypeChange(property: PropertyDefinition) {
    // Clear existing enum options when type changes
    property.enum = [];
    // Reset default value
    property.default = null;
    this.cdr.markForCheck();
  }

  // Methods for array default value management
  getArrayDefaultItems(property: PropertyDefinition): any[] {
    if (!Array.isArray(property.default)) {
      property.default = [];
    }
    return property.default;
  }

  addArrayDefaultItem(property: PropertyDefinition) {
    if (!Array.isArray(property.default)) {
      property.default = [];
    }

    // Create default value based on item type
    let defaultValue: any;
    const itemType = property.items?.type;

    switch (itemType) {
      case 'string':
        defaultValue = '';
        break;
      case 'integer':
      case 'number':
        defaultValue = 0;
        break;
      case 'boolean':
        defaultValue = false;
        break;
      case 'enum':
        // Use first enum value if available
        defaultValue = property.items?.enum?.[0]?.value ?? null;
        break;
      case 'object':
        defaultValue = {};
        break;
      default:
        defaultValue = null;
    }

    property.default.push(defaultValue);
    this.cdr.markForCheck();
  }

  removeArrayDefaultItem(property: PropertyDefinition, index: number) {
    if (Array.isArray(property.default)) {
      property.default.splice(index, 1);
      this.cdr.markForCheck();
    }
  }

  buildMenuItemsForIndex(index: number): MenuItem[] {
    const items: MenuItem[] = [];

    if (this.showOrderButtons) {
      items.push(
        {
          label: $localize`:@@openapi.moveUp:Move Up`,
          icon: 'pi pi-arrow-up',
          disabled: index === 0,
          command: () => this.movePropertyUp(index)
        },
        {
          label: $localize`:@@openapi.moveDown:Move Down`,
          icon: 'pi pi-arrow-down',
          disabled: index === this.properties.length - 1,
          command: () => this.movePropertyDown(index)
        }
      );
    }

    items.push({
      label: $localize`:@@common.edit:Edit`,
      icon: 'pi pi-pencil',
      command: () => this.editProperty(index)
    });

    if (this.showDeleteButton) {
      items.push(
        {
          separator: true
        },
        {
          label: $localize`:@@common.delete:Delete`,
          icon: 'pi pi-trash',
          command: () => this.removeProperty(index)
        }
      );
    }

    return items;
  }

  getActionsCount(index: number): number {
    return this.buildMenuItemsForIndex(index).filter(item => !item.separator).length;
  }

  togglePropertyMenu(event: Event, index: number) {
    this.currentPropertyIndex = index;
    this.propertyMenuItems = this.buildMenuItemsForIndex(index);
    this.propertyMenu.toggle(event);
  }

  getFillModeIcon(fillMode?: 'constant' | 'llm'): string {
    return fillMode === 'llm' ? 'pi pi-sparkles' : 'pi pi-lock';
  }

  getFillModeLabel(fillMode?: 'constant' | 'llm'): string {
    return fillMode === 'llm'
      ? $localize`:@@openapi.fillMode.ai:AI`
      : $localize`:@@openapi.fillMode.fixed:Fixed`;
  }

  formatDefault(value: unknown): string {
    if (value == null) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    const s = String(value);
    return s.length === 0 ? '-' : s;
  }

  onPropertyNameClick(property: PropertyDefinition): void {
    this.propertyNameClick.emit(property);
  }

  // Methods for nested object properties
  onNestedPropertiesChange(property: PropertyDefinition, updatedProperties: PropertyDefinition[]) {
    if (property.properties) {
      property.properties = updatedProperties;
      this.cdr.markForCheck();
    }
  }

  // Methods for array item type changes
  onArrayItemTypeChange(property: PropertyDefinition) {
    if (!property.items) return;

    // Clear default values when item type changes
    property.default = [];

    switch (property.items.type) {
      case 'string':
      case 'integer':
      case 'number':
      case 'boolean':
        delete property.items.properties;
        delete property.items.enum;
        delete property.items.enumType;
        break;
      case 'enum':
        property.items.enum = [{ label: 'Option 1', value: 'option1' }];
        property.items.enumType = 'string';
        delete property.items.properties;
        break;
      case 'object':
        property.items.properties = [];
        delete property.items.enum;
        delete property.items.enumType;
        break;
    }
    this.cdr.markForCheck();
  }

  onArrayItemPropertiesChange(property: PropertyDefinition, updatedProperties: PropertyDefinition[]) {
    if (property.items && property.items.properties) {
      property.items.properties = updatedProperties;
      this.cdr.markForCheck();
    }
  }

  addArrayEnumValue(property: PropertyDefinition) {
    if (!property.items || !property.items.enum) {
      if (property.items) {
        property.items.enum = [];
      }
    }
    if (property.items && property.items.enum) {
      const valueIndex = property.items.enum.length + 1;

      // Create value based on enumType
      let defaultValue: any;
      if (property.items.enumType === 'integer' || property.items.enumType === 'number') {
        defaultValue = valueIndex;
      } else {
        defaultValue = `option${valueIndex}`;
      }

      property.items.enum.push({ label: `Option ${valueIndex}`, value: defaultValue });
      this.cdr.markForCheck();
    }
  }

  removeArrayEnumValue(property: PropertyDefinition, index: number) {
    if (property.items && property.items.enum) {
      property.items.enum.splice(index, 1);
      this.cdr.markForCheck();
    }
  }

  onArrayEnumTypeChange(property: PropertyDefinition) {
    // Clear existing enum options when type changes
    if (property.items) {
      property.items.enum = [];
      // Reset default value array
      property.default = [];
      this.cdr.markForCheck();
    }
  }
}
